// WCAG 2.1 relative luminance / contrast ratio (https://www.w3.org/TR/WCAG21/#dfn-relative-luminance,
// https://www.w3.org/TR/WCAG21/#dfn-contrast-ratio) — used to pick an icon color that's readable
// against the tile's accent color (icon badge background).
const ICON_WHITE = '#ffffff';
const ICON_DARK = '#111827';

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
    const normalized = hex.replace('#', '');
    const value = normalized.length === 3
        ? normalized.split('').map((c) => c + c).join('')
        : normalized.padEnd(6, '0').slice(0, 6);
    const int = parseInt(value, 16);
    return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

function relativeLuminance(hex: string): number {
    const { r, g, b } = hexToRgb(hex);
    const [rLin, gLin, bLin] = [r, g, b].map((channel) => {
        const s = channel / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rLin + 0.7152 * gLin + 0.0722 * bLin;
}

function contrastRatio(hexA: string, hexB: string): number {
    const lA = relativeLuminance(hexA);
    const lB = relativeLuminance(hexB);
    const lighter = Math.max(lA, lB);
    const darker = Math.min(lA, lB);
    return (lighter + 0.05) / (darker + 0.05);
}

/** Wählt Weiß oder ein dunkles Grau als Icon-Farbe – je nachdem, was gegen die übergebene
 * Akzentfarbe (Hintergrund des Icon-Badges) den höheren WCAG-Kontrast ergibt. */
export function getAccessibleIconColor(backgroundHex: string | undefined | null): string {
    if (!backgroundHex) return ICON_WHITE;
    try {
        const whiteContrast = contrastRatio(backgroundHex, ICON_WHITE);
        const darkContrast = contrastRatio(backgroundHex, ICON_DARK);
        return darkContrast > whiteContrast ? ICON_DARK : ICON_WHITE;
    } catch {
        return ICON_WHITE;
    }
}
